import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  IconButton,
  TextField,
  InputAdornment,
  Button,
  Grid,
  CircularProgress,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
} from '@mui/icons-material';
import { DashboardLayout } from '../layout/DashboardLayout';
import { ContactModal, Contact } from './ContactModal';
import { useDatabase } from '../../hooks/useDatabase';

export const ContactList: React.FC = () => {
  const navigate = useNavigate();
  const db = useDatabase();
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [filterType, setFilterType] = useState<'all' | 'favorites'>('all');

  const loadContacts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const { data, error } = await db.getContacts();
      if (error) throw error;
      // Sort contacts by first name
      const sortedContacts = (data || []).sort((a, b) => {
        const aFirstName = a.name.split(' ')[0];
        const bFirstName = b.name.split(' ')[0];
        return aFirstName.localeCompare(bFirstName);
      });
      setContacts(sortedContacts);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load contacts');
    } finally {
      setLoading(false);
    }
  }, [db]);

  useEffect(() => {
    loadContacts();
  }, [loadContacts]);

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  const handleSubmit = async (contact: Partial<Omit<Contact, 'id' | 'created_at' | 'updated_at' | 'relevance_score'>>, organizationId?: string | null) => {
    try {
      if (selectedContact) {
        // Editing existing contact
        const { error } = await db.updateContact(selectedContact.id, contact);
        if (error) throw error;
        
        // Handle organization linking if provided
        if (organizationId !== undefined) {
          // Get current organizations
          const { data: currentOrgs } = await db.getContactOrganizations(selectedContact.id);
          const currentOrgIds = (currentOrgs || []).map(org => org.id);
          
          // If organizationId is provided and not already linked, link it
          if (organizationId && !currentOrgIds.includes(organizationId)) {
            await db.linkContactToOrganization(selectedContact.id, organizationId);
          }
        }
      } else {
        // Creating new contact
        const { data: newContact, error } = await db.createContact(contact);
        if (error) throw error;
        
        // Link organization if provided
        if (newContact && organizationId) {
          await db.linkContactToOrganization(newContact.id, organizationId);
        }
      }
      
      setOpenDialog(false);
      // Refresh the contacts list
      await loadContacts();
    } catch (err) {
      console.error('Error saving contact:', err);
      setError(err instanceof Error ? err.message : 'Failed to save contact');
    }
  };

  const handleAddContact = () => {
    setSelectedContact(null);
    setOpenDialog(true);
  };

  const handleEditContact = (event: React.MouseEvent, contact: Contact) => {
    event.stopPropagation();
    setSelectedContact(contact);
    setOpenDialog(true);
  };

  const handleDeleteContact = async (event: React.MouseEvent, contactId: string) => {
    event.stopPropagation();
    try {
      const { error } = await db.deleteContact(contactId);
      if (error) throw error;
      await loadContacts(); // Reload contacts after deletion
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete contact');
    }
  };

  const handleToggleFavorite = async (event: React.MouseEvent, contact: Contact) => {
    event.stopPropagation();
    try {
      const newFavoriteStatus = !contact.is_favorite;
      const { error } = await db.updateContact(contact.id, { is_favorite: newFavoriteStatus });
      if (error) throw error;
      // Update the contact in the local state
      setContacts(prevContacts =>
        prevContacts.map(c =>
          c.id === contact.id ? { ...c, is_favorite: newFavoriteStatus } : c
        )
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update favorite status');
    }
  };

  const handleContactClick = (contactId: string) => {
    navigate(`/contacts/${contactId}`);
  };

  const filteredContacts = contacts
    .filter((contact) => {
      // Filter by favorite status
      if (filterType === 'favorites' && !contact.is_favorite) {
        return false;
      }
      // Filter by search query
      return (
        contact.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (contact.email?.toLowerCase() || '').includes(searchQuery.toLowerCase()) ||
        (contact.phone && contact.phone.includes(searchQuery))
      );
    })
    .sort((a, b) => {
      const aFirstName = a.name.split(' ')[0];
      const bFirstName = b.name.split(' ')[0];
      return aFirstName.localeCompare(bFirstName);
    });

  const handleFilterChange = (_: React.MouseEvent<HTMLElement>, newFilter: 'all' | 'favorites' | null) => {
    if (newFilter !== null) {
      setFilterType(newFilter);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <CircularProgress />
        </Box>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Contacts">
      <Box sx={{ flexGrow: 1, p: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Grid container spacing={2}>
          {/* Header Section */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddContact}
                sx={{ borderRadius: 1, height: 32 }}
              >
                Add Contact
              </Button>
            </Box>
          </Grid>

          {/* Filter Buttons */}
          <Grid item xs={12}>
            <Paper sx={{ p: 1.5, mb: 2 }}>
              <ToggleButtonGroup
                value={filterType}
                exclusive
                onChange={handleFilterChange}
                size="small"
                sx={{ 
                  '& .MuiToggleButton-root': {
                    fontSize: '0.875rem',
                    px: 2,
                    py: 0.5,
                    minHeight: 32
                  }
                }}
              >
                <ToggleButton value="all">All Contacts</ToggleButton>
                <ToggleButton value="favorites">Favorite Contacts</ToggleButton>
              </ToggleButtonGroup>
            </Paper>
          </Grid>

          {/* Search Bar */}
          <Grid item xs={12}>
            <Paper sx={{ p: 1.5, mb: 2 }}>
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                placeholder="Search contacts..."
                value={searchQuery}
                onChange={handleSearch}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Paper>
          </Grid>

          {/* Contacts List */}
          <Grid item xs={12}>
            <Paper>
              <List sx={{ py: 0 }}>
                {filteredContacts.map((contact) => (
                  <ListItem
                    key={contact.id}
                    divider
                    onClick={() => handleContactClick(contact.id)}
                    sx={{
                      cursor: 'pointer',
                      py: 1,
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                      },
                    }}
                    secondaryAction={
                      <Box>
                        <IconButton
                          edge="end"
                          aria-label={contact.is_favorite ? 'remove from favorites' : 'add to favorites'}
                          onClick={(e) => handleToggleFavorite(e, contact)}
                          size="small"
                          sx={{ 
                            mr: 0.5,
                            color: contact.is_favorite ? '#ffc107' : 'text.secondary',
                            '&:hover': {
                              color: '#ffc107',
                            },
                          }}
                        >
                          {contact.is_favorite ? <StarIcon fontSize="small" /> : <StarBorderIcon fontSize="small" />}
                        </IconButton>
                        <IconButton
                          edge="end"
                          aria-label="edit"
                          onClick={(e) => handleEditContact(e, contact)}
                          size="small"
                          sx={{ mr: 0.5 }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          edge="end"
                          aria-label="delete"
                          onClick={(e) => handleDeleteContact(e, contact.id)}
                          size="small"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    }
                  >
                    <ListItemAvatar>
                      <Avatar sx={{ width: 32, height: 32, fontSize: '0.875rem' }}>{contact.name.charAt(0)}</Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {contact.name}
                        </Typography>
                      }
                      secondary={
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                          <Typography variant="caption" color="text.secondary">
                            {contact.email}
                          </Typography>
                          {contact.industry && (
                            <Typography variant="caption" color="text.secondary" sx={{ opacity: 0.7 }}>
                              {contact.industry}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </Paper>
          </Grid>
        </Grid>

        <ContactModal
          open={openDialog}
          onClose={() => setOpenDialog(false)}
          onSubmit={handleSubmit}
          contact={selectedContact}
        />
      </Box>
    </DashboardLayout>
  );
}; 